import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { RootState } from '../index'
import { getCookieAuthHeaders } from '@utils/csrfCookie'

export interface LoginRequest {
  email: string
  password: string
  rememberMe?: boolean
}

export interface RegisterRequest {
  email: string
  username: string
  password: string
  firstName?: string
  lastName?: string
}

export interface AuthResponse {
  user: {
    id: string
    email: string
    username: string
    firstName?: string
    lastName?: string
    profileImageUrl?: string
    subscriptionTier: 'free' | 'premium' | 'pro'
    role?: 'user' | 'admin' | 'super_admin' | 'moderator'
    createdAt: string
    updatedAt: string
  }
  accessToken: string
}

export interface RefreshTokenResponse {
  accessToken: string
}

export interface UpdateProfileRequest {
  firstName?: string
  lastName?: string
  profileImageUrl?: string
}

export interface AuthConfigResponse {
  googleOAuth: boolean
}

export const authApi = createApi({
  reducerPath: 'authApi',
  baseQuery: fetchBaseQuery({
    baseUrl: '/api/v1/auth',
    credentials: 'include',
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token
      if (token) {
        headers.set('authorization', `Bearer ${token}`)
      }
      return headers
    },
  }),
  tagTypes: ['User'],
  endpoints: (builder) => ({
    login: builder.mutation<AuthResponse, LoginRequest>({
      query: (credentials) => ({
        url: '/login',
        method: 'POST',
        body: credentials,
      }),
      transformResponse: (response: { success: boolean; data: AuthResponse }) => response.data,
      invalidatesTags: ['User'],
    }),
    register: builder.mutation<AuthResponse, RegisterRequest>({
      query: (userData) => ({
        url: '/register',
        method: 'POST',
        body: userData,
      }),
      transformResponse: (response: { success: boolean; data: AuthResponse }) => response.data,
      invalidatesTags: ['User'],
    }),
    refreshToken: builder.mutation<RefreshTokenResponse, void>({
      query: () => ({
        url: '/refresh',
        method: 'POST',
        headers: getCookieAuthHeaders(),
      }),
      transformResponse: (response: { success: boolean; data: RefreshTokenResponse }) => response.data,
    }),
    logout: builder.mutation<void, void>({
      query: () => ({
        url: '/logout',
        method: 'POST',
        headers: getCookieAuthHeaders(),
      }),
      invalidatesTags: ['User'],
    }),
    getProfile: builder.query<AuthResponse['user'], void>({
      query: () => '/profile',
      transformResponse: (response: { success: boolean; data: AuthResponse['user'] }) => response.data,
      providesTags: ['User'],
    }),
    updateProfile: builder.mutation<AuthResponse['user'], UpdateProfileRequest>({
      query: (updates) => ({
        url: '/profile',
        method: 'PUT',
        body: updates,
      }),
      transformResponse: (response: { success: boolean; data: AuthResponse['user'] }) => response.data,
      invalidatesTags: ['User'],
    }),
    forgotPassword: builder.mutation<void, { email: string }>({
      query: (body) => ({
        url: '/forgot-password',
        method: 'POST',
        body,
      }),
    }),
    resetPassword: builder.mutation<void, { token: string; password: string }>({
      query: (body) => ({
        url: '/reset-password',
        method: 'POST',
        body,
      }),
    }),
    confirmEmailChange: builder.mutation<{ email: string }, { token: string }>({
      query: (body) => ({
        url: '/confirm-email-change',
        method: 'POST',
        body,
      }),
      transformResponse: (response: { success: boolean; data: { email: string } }) => response.data,
    }),
    changePassword: builder.mutation<{ accessToken: string }, { currentPassword: string; newPassword: string }>({
      query: (body) => ({
        url: '/change-password',
        method: 'PUT',
        body,
      }),
      transformResponse: (response: { success: boolean; data: { accessToken: string } }) => response.data,
    }),
    getAuthConfig: builder.query<AuthConfigResponse, void>({
      query: () => '/config',
      transformResponse: (response: { success: boolean; data: AuthConfigResponse }) => response.data,
    }),
  }),
})

export const {
  useLoginMutation,
  useRegisterMutation,
  useRefreshTokenMutation,
  useLogoutMutation,
  useGetProfileQuery,
  useUpdateProfileMutation,
  useForgotPasswordMutation,
  useResetPasswordMutation,
  useConfirmEmailChangeMutation,
  useChangePasswordMutation,
  useGetAuthConfigQuery,
} = authApi
